import { useState } from 'react'
import { CheckCircle, Loader } from 'lucide-react'
import { supportTicketService, type PublicTicketCreate } from '../services/supportTicketService'
import type { TicketCategory, TicketPriority } from '../types/api.types'

const CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: 'TECHNICAL', label: 'Technical Issue' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'FEATURE_REQUEST', label: 'Feature Request' },
  { value: 'OTHER', label: 'Other' },
]

const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
]

export function SupportPage(): JSX.Element {
  const [form, setForm] = useState<PublicTicketCreate>({
    submitted_by_name: '',
    submitted_by_email: '',
    submitted_by_phone: '',
    organization_name: '',
    category: 'TECHNICAL',
    priority: 'MEDIUM',
    subject: '',
    description: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [ticketNumber, setTicketNumber] = useState('')

  function set(key: keyof PublicTicketCreate, value: string) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true); setError('')
    try {
      const ticket = await supportTicketService.submitPublic({
        ...form,
        submitted_by_phone: form.submitted_by_phone || undefined,
        organization_name: form.organization_name || undefined,
      })
      setTicketNumber(ticket.ticket_number)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit ticket. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (ticketNumber) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <CheckCircle size={28} className="text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Ticket Submitted</h1>
          <p className="mt-2 text-[14px] text-gray-500">Your reference number is:</p>
          <p className="mt-2 font-mono text-[20px] font-bold text-brand-green">{ticketNumber}</p>
          <p className="mt-3 text-[13px] text-gray-500">
            A confirmation email has been sent to <strong>{form.submitted_by_email}</strong>.
            Our support team will get back to you shortly.
          </p>
          <button
            type="button"
            onClick={() => {
              setTicketNumber('')
              setForm({
                submitted_by_name: '',
                submitted_by_email: '',
                submitted_by_phone: '',
                organization_name: '',
                category: 'TECHNICAL',
                priority: 'MEDIUM',
                subject: '',
                description: '',
              })
            }}
            className="mt-6 rounded-lg border border-gray-200 px-4 py-2 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
          >
            Submit another ticket
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-gray-50 py-12 px-4">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Contact Support</h1>
          <p className="mt-2 text-[14px] text-gray-500">
            Describe your issue and we'll get back to you as soon as possible.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl bg-white p-8 shadow-sm space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Your Name *</label>
              <input
                required
                value={form.submitted_by_name}
                onChange={(e) => set('submitted_by_name', e.target.value)}
                placeholder="Ahmad Hassan"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Email *</label>
              <input
                required
                type="email"
                value={form.submitted_by_email}
                onChange={(e) => set('submitted_by_email', e.target.value)}
                placeholder="you@company.com"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Phone</label>
              <input
                value={form.submitted_by_phone}
                onChange={(e) => set('submitted_by_phone', e.target.value)}
                placeholder="+971501234567"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Organisation Name</label>
              <input
                value={form.organization_name}
                onChange={(e) => set('organization_name', e.target.value)}
                placeholder="Your company name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Category *</label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Priority *</label>
              <select
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">Subject *</label>
            <input
              required
              value={form.subject}
              onChange={(e) => set('subject', e.target.value)}
              placeholder="Brief summary of the issue"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
            />
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">Description *</label>
            <textarea
              required
              rows={5}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Please describe the issue in detail — include any error messages, steps to reproduce, and when it started happening..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-80 disabled:opacity-50"
          >
            {submitting && <Loader size={14} className="animate-spin" />}
            Submit Ticket
          </button>
        </form>

        <p className="mt-4 text-center text-[12px] text-gray-400">
          Already have an account? <a href="/login" className="text-brand-green hover:underline">Log in</a> to track your tickets.
        </p>
      </div>
    </div>
  )
}
